import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * RoleRoute — restricts a route subtree to one or more roles.
 *
 * Usage:
 *   <RoleRoute role="admin"> ... </RoleRoute>
 *   <RoleRoute roles={['admin','crew_lead']}> ... </RoleRoute>
 *
 * Behavior:
 *   - If auth is still loading -> spinner.
 *   - If no user -> redirect to /login (defense in depth; usually
 *     ProtectedRoute already covers this).
 *   - If profile is still loading -> spinner.
 *   - If role mismatch -> show a clean "Access restricted" screen with a
 *     Back button. We deliberately do NOT silently redirect because we want
 *     the user to understand they hit a permission boundary rather than a
 *     broken link.
 *
 *   - Fails CLOSED: when profile fetch failed (RLS error / 007 not applied)
 *     the profile is null, role defaults to 'crew_lead', so any admin-gated
 *     route blocks instead of leaking.
 */
export const RoleRoute = ({ role, roles, fallback = '/dashboard/crew-clock', children }) => {
  const { user, loading, profile, profileLoading, role: currentRole } = useAuth();
  const location = useLocation();

  const allowed = (() => {
    const list = Array.isArray(roles) ? roles : role ? [role] : [];
    if (list.length === 0) return false;
    return list.includes(currentRole);
  })();

  if (loading || profileLoading) {
    return (
      <div
        className="min-h-[40vh] flex items-center justify-center"
        data-testid="role-route-loading"
      >
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm tracking-wide uppercase">Verifying access</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (allowed) return children;

  // Role mismatch screen.
  return (
    <div
      className="min-h-[60vh] flex items-center justify-center px-4"
      data-testid="role-route-denied"
    >
      <Card className="max-w-md w-full border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-[hsl(var(--destructive))]/15 flex items-center justify-center">
              <ShieldAlert className="h-5 w-5 text-[hsl(var(--destructive))]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Restricted area
              </div>
              <h2 className="text-lg font-semibold text-foreground">Admin only</h2>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            This module is reserved for the business owner. Your account is
            signed in as <span className="text-foreground font-medium">{profile?.full_name || user?.email}</span>{' '}
            with role <span className="text-foreground font-medium">{currentRole || 'crew_lead'}</span>.
          </p>
          <p className="text-xs text-muted-foreground">
            If you believe this is a mistake, ask the company owner to promote your
            profile to <span className="font-mono">admin</span> in Supabase.
          </p>
          <div className="flex gap-2 pt-1">
            <Button
              asChild
              variant="default"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="role-route-back-btn"
            >
              <a href={fallback}>Return to safe area</a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RoleRoute;
